<script setup lang="ts">
  import {computed, ref} from 'vue';
  import {useI18n} from 'vue-i18n';
  import useCpBem from '~/composables/useCpBem';
  import CpModal from '~/components/cp/CpModal/CpModal.vue';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpButton from '~/components/cp/CpButton/CpButton.vue';
  import CpTextField from '~/components/cp/CpTextField/CpTextField.vue';
  import CpIconStandard from '~/components/cp/CpIcon/CpIconStandard/CpIconStandard.vue';
  import CpSectionHeader from '~/components/cp/CpSection/CpSectionHeader/CpSectionHeader.vue';
  import {NUMBER_REGEX} from '~/utils';

  const {b, e} = useCpBem('cp-modal-data-entry-product-qty');
  const {t} = useI18n();

  type CpModalDataEntryProductQtyProps = {
    kind?: 'stock'|'warranty'|'return'|'openedPackage'|'productNotReceived'
    isDigital: boolean;
    stock: number;
    hideLowStockLabel?: boolean
  };

  const show = defineModel<boolean>('show', {default: false});

  const props = withDefaults(defineProps<CpModalDataEntryProductQtyProps>(),
      {kind: 'stock'});

  const lowStock = computed(() => {
    return !props.isDigital && props.stock <= 10 && !props.hideLowStockLabel;
  });

  const qty = ref<string>('');
  const form = ref<any>(null);

  const emit = defineEmits<{
    (e: 'update:show', value: boolean): void
    (e: 'add-qty', {qty}: { qty: number }): void
  }>();

  const rules = [
    (v: string) => !!v || t('emptyQty'),
    (v: string) => NUMBER_REGEX.test(v) || t('emptyQty'),
    (v: string) => (Number.parseFloat(v) > 0) || t('emptyQty'),
    (v: string) => Number.parseFloat(v) <= props.stock || t('notEnoughStock'),
  ];

  const title = {
    openedPackage: t('selectQtyOfPiecesWithOpenedPackage'),
    productNotReceived: t('selectQtyOfPiecesThatYouNotReceived'),
    return: t('selectQtyOfPiecesToRequestReturn'),
    warranty: t('selectQtyOfPiecesToRequestWarranty'),
    stock: t('selectQtyOfPieces'),
  };

  const applyEvent = async () => {
    const v = await form.value?.validate();
    if (v?.valid) {
      emit('add-qty', {qty: Number.parseInt(qty.value)});
      emit('update:show', false);
    }
  };

</script>

<template>
    <div :class="b">
        <CpModal v-model:show="show" @close-modal="emit('update:show', false)">
            <CpSectionHeader
                :class="e`section-header`"
                :title="title[kind]"
                button="close"
                type="h3"
                @header-action="emit('update:show', false)"
            />
            <v-form ref="form" :class="e`width`" @submit.prevent>
                <div :class="e`body-content`">
                    <div v-if="lowStock && kind==='stock'" :class="e`last-pieces-content`">
                        <CpIconStandard icon="timer_outline" type="mkt" />
                        <CpText type="mkt-dark" variant="body-bold">
                            {{ t('lastPieces', { count: stock }) }}
                        </CpText>
                    </div>

                    <CpText v-else type="neutral" variant="body-regular">
                        {{ isDigital ? t('stockAvailable') : t('onlyPiecesRemain', { count: stock }) }}
                    </CpText>

                    <CpTextField
                        id="qty"
                        :model-value="qty"
                        data-testid="qty"
                        :class="e`qty-input`"
                        :label="t('qty')"
                        :required="true"
                        type="number"
                        :rules="rules"
                        @update:model-value="qty = $event"
                    />
                </div>

                <div :class="e`footer-content`">
                    <CpButton
                        :class="e`apply-button`"
                        data-testid="button"
                        :label="t('apply')"
                        :full-width="true"
                        type="submit"
                        color="primary"
                        kind="label"
                        :hollow="true"
                        @click="applyEvent"
                    />
                </div>
            </v-form>
        </CpModal>
    </div>
</template>

<style scoped lang="scss">
  .cp-modal-data-entry-product-qty {

    $background-color: var(--color-white);

    &__body-content {
      padding: $cp-gutter;
      gap: $cp-gutter;
      display: flex;
      flex-direction: column;
      border-bottom: $cp-border-standard;
    }

    &__footer-content {
      padding: $cp-gutter;
    }

    &__last-pieces-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: $cp-gutter-xsmall;
    }

    &__qty-input {
      width: 100%;
    }

    &__qty-input:deep(input::-webkit-outer-spin-button),
    &__qty-input:deep(input::-webkit-inner-spin-button)  {
      -webkit-appearance: none;
    }

    &__width {
      width: 100%;
    }
  }
</style>

<i18n locale="es-US" lang="yaml">
  selectQtyOfPieces: 'Seleccionar cantidad de piezas'
  selectQtyOfPiecesToRequestWarranty: 'Selecciona la cantidad de piezas a solicitar garantía'
  selectQtyOfPiecesToRequestReturn: 'Selecciona la cantidad de piezas a solicitar devolución'
  selectQtyOfPiecesWithOpenedPackage: 'Selecciona la cantidad de piezas con empaque abierto'
  selectQtyOfPiecesThatYouNotReceived: 'Selecciona la cantidad de piezas que no recibiste'
  onlyPiecesRemain: '{count} piezas disponibles'
  lastPieces: '¡Solo quedan {count} piezas!'
  stockAvailable: "En existencia"
  qty: 'Ingresar cantidad'
  apply: 'Aplicar'
  emptyQty: 'Ingresa una cantidad de piezas.'
  notEnoughStock: 'Sin stock suficiente.'
</i18n>
